import { Ellipsis } from 'lucide-react';
import system_setting_icon from 'images/setting_icons/system_setting_icon.svg';
import * as React from 'react';
import { useHistory } from 'react-router';
import { getFormTemplates } from '../../atoms/forms';
import Add from '../../components_v2/add/Add';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import { Column, Table } from '../../components_v2/table/Table';
import { DarkGreySidely, GreySidely3, LightGreySidely } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { useEditFormsPermission } from '../../utils/customHooks';
import { AlertContext, AlertRes } from '../alert/AlertProvider';
import { DateAccessor } from '../client-companies/data/CompanyColumns';
import { ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { ComponentProps } from '../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../import/model';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import { TableRow } from '../orders/templateOrders/style/Style';
import { deleteFormView, getAllFormViews, View } from './actions';
import { FormTemplate } from './jsonValidator';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

export default function FormViewsList(props: ComponentProps) {
	const history = useHistory();
	const isAllowedToEditForms = useEditFormsPermission();
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [formViews, setFormViews] = React.useState<View[]>([]);
	const [templates, setTemplates] = React.useState<FormTemplate[]>([]);

	const { alertDelete } = React.useContext(AlertContext);

	const refresh = React.useCallback(async() => {
		try {
			setLoadingState(LoadingStateEnum.LOADING);
			const formViewsRes = await getAllFormViews();
			const templatesRes = await getFormTemplates();
			setFormViews(formViewsRes);
			setTemplates(templatesRes.allForms);
			setLoadingState(LoadingStateEnum.LOADED);
		} catch {
			setLoadingState(LoadingStateEnum.ERROR);
		}
	}, []);

	React.useEffect(() => {
		refresh();
	}, []);

	React.useEffect(() => {
		refresh();
	}, [history.location.pathname, refresh]);

	React.useEffect(() => {
		props.setToolBarState({
			title: translateToString('forms'),
			bottomRightToolbarComponent: <>
				{isAllowedToEditForms &&
					<Add size='22px' onClick={() => history.push({ pathname: '/form-view-template' })} />
				}
			</>
		});
	}, [isAllowedToEditForms]);

	const list: DropdownData[] = [
		{
			label: translateToString('global.quick_action.edit'),
			value: 'EDIT'
		},
		{
			label: translateToString('global.quick_action.delete'),
			value: 'DELETE'
		}
	];

	const columns: Array<Column<View>> = React.useMemo(() => [
		{
			id: 'name',
			Header: translateToString('name'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
			>
				{row.name}
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		},
		{
			id: 'linked_to',
			Header: translateToString('linked_to'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
				title={row.linked_form_ids.map((id) => {
					const name = templates.find((template) => template.id === id)?.name;
					return name ?? '-';
				}).join(', ')}
			>
				{row.linked_form_ids.map((id) => {
					const name = templates.find((template) => template.id === id)?.name;
					return name ?? '-';
				}).join(', ')}
			</TableRow>,
			width: undefined,
			minWidth: 180,
			type: 'string',
		},
		{
			id: 'sequence',
			Header: translateToString('sequence'),
			accessor: row => <TableRow><div style={{ backgroundColor: LightGreySidely }}>
				{row.sequence}
			</div>
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		},
		{
			id: 'created_at',
			Header: translateToString('created_at'),
			accessor: row => <DateAccessor date={row.created_at} utc={true} />,
			width: undefined,
			minWidth: 150,
			type: 'temporal',
		},
		{
			id: 'updated_at',
			Header: translateToString('updated_at'),
			accessor: row => <DateAccessor date={row.updated_at} utc={true} />,
			width: undefined,
			minWidth: 150,
			type: 'temporal',
		}, {
			id: 'quick_actions',
			noHeaderEllipsis: true,
			Header: <img src={system_setting_icon} style={{ width: 18 }} />,
			accessor: row =>
				<Dropdown
					name='quick_action'
					datalist={list}
					readOnly
					searchable={false}
					dropdownStyle={{ width: '10px', optionWidth: '100px', fontSize: 13, optionLeft: '-112px', containerTop: '-6px' }}
					JSXButton={() =>
						<ToolbarImageContainer><Ellipsis color={GreySidely3} strokeWidth={2.5} size={18} /></ToolbarImageContainer>
					}
					onChange={value => {
						if (value.value === 'DELETE') {
							alertDelete({
								zIndex: 201,
								name: row.name
							})
								.then(result => result.res === AlertRes.Ok ? deleteFormView(row.id).then(() => refresh()) : undefined);
						}
						else if (value.value === 'EDIT') {
							history.push({
								pathname: 'form-view-template',
								search: `?id=${row.id}`
							});
						}
					}}
				/>,
			width: 40,
			minWidth: 40,
			disableSortBy: true,
			disableFilter: true,
			unresizeable: true,
			freeze: 'right'
		}
	], [isAllowedToEditForms, templates, formViews]);
	return <>
		<Table
			height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
			columns={columns}
			data={formViews}
		/>
		<ComponentLoader allScreen loadingState={loadingState} />
	</>;
}
